import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Avatar,
    Badge,
    Box,
    Button,
    Heading,
    HStack,
    Icon,
    IconButton,
    SimpleGrid,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { MdContentCopy, MdInfo, MdLocalHospital, MdLocationOn, MdPerson, MdVerifiedUser } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { fetchPhysicianById, verifyPhysicianSignature } from '../../../api';
import { hasPermission } from '../../../Utils';

export default function PhysicianProfile() {
    const { id } = useParams();
    const [physician, setPhysician] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedSignatureId, setSelectedSignatureId] = useState(null);
    const [selectedSignatureImage, setSelectedSignatureImage] = useState(null);

    const [isVerifying, setIsVerifying] = useState(false);
    const cancelRef = useRef();
    const toast = useToast()

    useEffect(() => {
        const load = async () => {
            const res = await fetchPhysicianById(id);
            if (res.success) setPhysician(res.data);
        };
        load();
    }, [id]);

    const handleVerifySignature = async (signatureId) => {
        setIsVerifying(true);
        const res = await verifyPhysicianSignature(physician._id, signatureId);
        if (res.success) {
            toast({
                title: 'Signature verified.',
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            setPhysician((prev) => ({
                ...prev,
                signature: prev.signature.map((sig) =>
                    sig._id === signatureId
                        ? {
                            ...sig,
                            verified: true,
                        }
                        : sig
                ),
            }));

        } else {
            toast({
                title: 'Failed to verify.',
                description: res.error,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
        setIsVerifying(false);
    };


    if (!physician) return <Text>Loading...</Text>;

    return (
        <Box p={8}>
            <HStack spacing={6} mb={8}>
                <Avatar size="xl" name={physician.fullName} />
                <VStack align="start">
                    <Heading size="lg">{physician.fullName}</Heading>
                    <Text><b>Specialty:</b> {physician.specialty || 'N/A'}</Text>
                    <Text><b>NPI Number:</b> {physician.npiNumber || 'N/A'}</Text>
                    <HStack>
                        <Text fontSize="sm" color="gray.500">Internal ID: {physician._id}</Text>
                        <IconButton
                            size="xs"
                            variant="ghost"
                            icon={<MdContentCopy />}
                            aria-label="Copy ID"
                            onClick={() => navigator.clipboard.writeText(physician._id)}
                        />
                    </HStack>
                </VStack>
            </HStack>

            <SimpleGrid templateColumns={{ base: '1fr', md: '3fr 1fr' }} spacing={8}>
                <Box>
                    {/* Contact Info */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}><Icon as={MdPerson} mr={2} />Contact Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Text><b>Email:</b> {physician.contact?.email || 'N/A'}</Text>
                            <Text><b>Phone:</b> {physician.contact?.phone || 'N/A'}</Text>
                            <Text><b>Fax:</b> {physician.contact?.fax || 'N/A'}</Text>
                        </SimpleGrid>
                    </Box>

                    {/* License */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}><Icon as={MdVerifiedUser} mr={2} />License Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Text><b>License Number:</b> {physician.license?.number || 'N/A'}</Text>
                            <Text><b>Issuing State:</b> {physician.license?.issuingState || 'N/A'}</Text>
                            <Text><b>Expiration Date:</b> {physician.license?.expirationDate ? new Date(physician?.license?.expirationDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : 'N/A'}</Text>
                        </SimpleGrid>
                    </Box>

                    {/* Address */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}><Icon as={MdLocationOn} mr={2} />Practice Address</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Text><b>Street:</b> {physician.practiceAddress?.address || 'N/A'}</Text>
                            <Text><b>City:</b> {physician.practiceAddress?.city || 'N/A'}</Text>
                            <Text><b>State:</b> {physician.practiceAddress?.state || 'N/A'}</Text>
                            <Text><b>ZIP:</b> {physician.practiceAddress?.zip || 'N/A'}</Text>
                        </SimpleGrid>
                    </Box>

                    {/* Affiliation & IDs */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}><Icon as={MdLocalHospital} mr={2} />Affiliation & IDs</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Text><b>Affiliated Facility:</b> {physician.affiliatedFacility?.name || 'N/A'}</Text>
                            <Text><b>DEA Number:</b> {physician.deaNumber || 'N/A'}</Text>
                            <Text><b>Taxonomy Code:</b> {physician.taxonomyCode || 'N/A'}</Text>
                            <Text><b>Medicaid ID:</b> {physician.medicaidId || 'N/A'}</Text>
                            <Text><b>Medi-Care ID:</b> {physician.medicareId || 'N/A'}</Text>
                        </SimpleGrid>
                    </Box>

                    {/* Metadata */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}><Icon as={MdInfo} mr={2} />Metadata Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Text><b>Created At:</b> {physician.createdAt ? new Date(physician.createdAt).toLocaleString() : 'N/A'}</Text>
                            <Text><b>Created By:</b> {physician.createdBy?.firstName ? `${physician.createdBy?.firstName} ${physician.createdBy?.lastName}` : 'N/A'}</Text>
                            <Text><b>Last Updated:</b> {physician.updatedAt ? new Date(physician.updatedAt).toLocaleString() : 'N/A'}</Text>
                        </SimpleGrid>
                    </Box>


                </Box>

                {/* Signature Info */}
                <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                    <Heading size="md" mb={4}>Digital Signature</Heading>
                    {/* <Box spacing={4}>
                        <Text mt={5}><b>Phone:</b> {physician.signature?.phone || 'N/A'}</Text>
                        <Text mt={5}><b>Last Sent:</b> {physician.signature?.lastSent ? dayjs(physician.signature.lastSent).format('MMM D, YYYY h:mm A') : 'N/A'}</Text>
                        <Text mt={5}><b>Attempts:</b> {physician.signature?.attempts}</Text>
                        <Text mt={5}><b>Verified:</b> {physician.signature?.verified ? 'Yes' : 'No'}</Text>
                    </Box> */}
                    {physician.signature?.length > 0 && (
                        <Box mt={5}>
                            <Text mb={2}><b>Signatures:</b></Text>
                            {[...physician.signature]
                                .filter(sig => !!sig.image)
                                .sort((a, b) => new Date(b.metadata?.signedAt) - new Date(a.metadata?.signedAt))
                                .map((sig, index) => (
                                    <Box key={index} mb={4} border="1px solid #ccc" p={3} borderRadius="md">
                                        <HStack borderBottom={"1px solid #ccc"} pb={4} mb={7} justify="space-between">
                                            <Text fontSize="sm" color="gray.600">
                                                Signed {sig.metadata?.signedAt ? new Date(sig.metadata.signedAt).toLocaleDateString() : 'N/A'}
                                            </Text>
                                            {index === 0 && <Badge colorScheme="green">Current</Badge>}
                                            {sig.verified ? <Badge colorScheme="green">Verified</Badge> : <Badge colorScheme="red">Not-verified</Badge>}



                                        </HStack>

                                        <img src={sig.image} alt={`Signature ${index + 1}`} style={{ maxWidth: '100%', height: 'auto' }} />

                                        <HStack mt={7} borderTop={"1px solid #ccc"} pt={4} spacing={3}>
                                            <Button
                                                size="xs"
                                                colorScheme="green"
                                                onClick={() => {
                                                    const link = document.createElement('a');
                                                    link.href = sig.image;
                                                    link.download = `${physician?.fullName}_signature_${index + 1}.png`;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }}
                                            >
                                                Download Signature
                                            </Button>

                                            {(!sig.verified && hasPermission('verifySignatures')) && (
                                                <Button
                                                    size="xs"
                                                    colorScheme="blue"
                                                    onClick={() => {
                                                        setSelectedSignatureId(sig._id);
                                                        setSelectedSignatureImage(sig.image);
                                                        setIsConfirmOpen(true);
                                                    }}
                                                >
                                                    Verify
                                                </Button>
                                            )}
                                        </HStack>

                                        {sig.verified && sig.verifiedBy?.user && (
                                            <Text mt={2} fontSize="xs" color="gray.500">
                                                Verified by: {sig.verifiedBy.user.firstName} {sig.verifiedBy.user.lastName} on {new Date(sig.verifiedBy.date).toLocaleString()}
                                            </Text>
                                        )}
                                    </Box>
                                ))}
                        </Box>
                    )}

                </Box>
            </SimpleGrid>


            {selectedSignatureId &&
                <AlertDialog
                    isOpen={isConfirmOpen}
                    leastDestructiveRef={cancelRef}
                    onClose={() => setIsConfirmOpen(false)}
                >
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                Verify Signature
                            </AlertDialogHeader>

                            <AlertDialogBody>
                                Are you sure you want to verify this signature?
                                <Box mt={8} mb={8}>
                                    <img src={selectedSignatureImage} alt={`Signature`} style={{ maxWidth: '100%', height: 'auto' }} />
                                </Box>

                            </AlertDialogBody>

                            <AlertDialogFooter>
                                <Button ref={cancelRef} onClick={() => setIsConfirmOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme="blue"
                                    onClick={async () => {
                                        await handleVerifySignature(selectedSignatureId);
                                        setIsConfirmOpen(false);
                                    }}

                                    isLoading={isVerifying}
                                    loadingText="Verifying..."

                                    ml={3}
                                >
                                    Confirm
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>

            }


        </Box>
    );
}
